import { AxiosError } from 'axios';

export enum ERROR_CODES {
	GENERIC = 'GENERIC',
	SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
	REQUEST_ABORTED = 'REQUEST_ABORTED',
}

export const UNAUTHORIZED_FALLBACK_MESSAGE =
	'You do not have permission to access this content. Please contact your account administrator for more information.';

export const getApiErrorMessage = (apiError: Partial<ApiError> | undefined, status?: number) => {
	if (apiError?.message) {
		return apiError.message;
	}

	if (status === 403) {
		return UNAUTHORIZED_FALLBACK_MESSAGE;
	}

	return 'Sorry, an error occurred.';
};

export type ErrorConfig = {
	code: ERROR_CODES | string;
	status?: number;
	message: string;
	apiError?: ApiError;
	axiosError?: AxiosError;
	requestUrl?: string;
};

export type ApiError = {
	code: string;
	stackTrace: string;
	message: string;
	globalErrors?: string;
	fieldErrors?: FieldError[];
	metaData?: Record<string, string | boolean>;
};

type FieldError = {
	field: string;
	error: string;
};

// The API says there's no valid login (no cookie, or an expired token)
export const isSignedOutError = (error: unknown) => {
	const errorConfig = error as Partial<ErrorConfig> | undefined;

	return (
		errorConfig?.code === 'AUTHENTICATION_REQUIRED' ||
		errorConfig?.code === 'NotAuthorizedException' ||
		errorConfig?.status === 401
	);
};

export type ErrorGeneratorFuction = (error: AxiosError) => ErrorConfig;
export type ErrorsConfig = Record<ERROR_CODES, ErrorGeneratorFuction>;

export const errors: ErrorsConfig = {
	[ERROR_CODES.GENERIC]: (error) => {
		const status = error.response?.status;

		return {
			code: ERROR_CODES.GENERIC,
			...(status && { status }),
			message: getApiErrorMessage(undefined, status),
			axiosError: error,
			requestUrl: error.config?.url,
		};
	},
	[ERROR_CODES.SERVICE_UNAVAILABLE]: (error) => {
		return {
			code: ERROR_CODES.SERVICE_UNAVAILABLE,
			status: 503,
			message: 'Service is currently unavailable',
			axiosError: error,
			requestUrl: error.config?.url,
		};
	},
	[ERROR_CODES.REQUEST_ABORTED]: (error) => {
		const status = error.response?.status;

		return {
			code: ERROR_CODES.REQUEST_ABORTED,
			...(status && { status }),
			message: 'Sorry, the request was cancelled.',
			axiosError: error,
			requestUrl: error.config?.url,
		};
	},
};
